import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import LogoImage from 'assets/img/logos/LogoImage.png';
import 'components/registration-shared/registration.css';

/**
 * Split layout for the public, signed-out screens: /register,
 * /set-password/:hash, and (through SignInLayout) sign in, forgot, change and
 * reset password. Rendered outside the authenticated MainLayout. Left: a brand
 * panel with either product highlights (`features`, on /register and sign-in)
 * or progress steps (`steps`, on /set-password). The panel is hidden below
 * lg; there, only steps get a compact strip. Right: the form card —
 * `size="narrow"` for short forms like sign-in — with an optional `footer`
 * under it. The logo (both panel and phone versions) goes back to sign-in.
 */

const SIGN_IN_PATH = '/';

const itemPropType = PropTypes.shape({
  icon: PropTypes.oneOfType([PropTypes.object, PropTypes.array]).isRequired,
  title: PropTypes.string.isRequired,
  shortTitle: PropTypes.string,
  text: PropTypes.node,
  done: PropTypes.bool
});

const RegistrationLayout = ({
  title,
  subtitle = null,
  hero = null,
  features = [],
  steps = [],
  size = 'wide',
  footer = null,
  children
}) => (
  <div className="mym-reg">
    <div className="mym-reg-shell">
      <aside className="mym-reg-aside" aria-label="About MapYourMeds">
        <div className="mym-reg-aside-inner">
          <Link
            to={SIGN_IN_PATH}
            className="mym-reg-brand"
            aria-label="MapYourMeds — go to the sign-in page"
          >
            <img src={LogoImage} alt="MapYourMeds" />
          </Link>
          <h1 className="mym-reg-aside-title">{title}</h1>
          {subtitle && <p className="mym-reg-aside-subtitle">{subtitle}</p>}
          {hero && (
            <div
              className={classNames('mym-reg-hero', {
                'is-alone': features.length === 0 && steps.length === 0
              })}
            >
              {hero}
            </div>
          )}
          {/* Product highlights: same look as the steps, but not a sequence. */}
          {features.length > 0 && (
            <ul className="mym-reg-steps">
              {features.map(feature => (
                <li key={feature.title}>
                  <span className="mym-reg-step-icon" aria-hidden="true">
                    <FontAwesomeIcon icon={feature.icon} />
                  </span>
                  <div>
                    <div className="mym-reg-step-title">{feature.title}</div>
                    <div className="mym-reg-step-text">{feature.text}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
          {steps.length > 0 && (
            <ol className="mym-reg-steps">
              {steps.map(step => (
                <li key={step.title}>
                  <span
                    className={classNames('mym-reg-step-icon', {
                      'is-done': step.done
                    })}
                    aria-hidden="true"
                  >
                    <FontAwesomeIcon icon={step.icon} />
                  </span>
                  <div>
                    <div className="mym-reg-step-title">{step.title}</div>
                    <div className="mym-reg-step-text">{step.text}</div>
                  </div>
                </li>
              ))}
            </ol>
          )}
          <div className="mym-reg-aside-foot">
            Questions about access? Contact your agency administrator.
          </div>
        </div>
      </aside>

      <main
        className={classNames('mym-reg-main', {
          'is-narrow': size === 'narrow'
        })}
      >
        <div className="mym-reg-main-inner">
          <div className="mym-reg-mobile-brand">
            <Link
              to={SIGN_IN_PATH}
              className="mym-reg-mobile-brand-link"
              aria-label="MapYourMeds — go to the sign-in page"
            >
              <img src={LogoImage} alt="MapYourMeds" />
            </Link>
          </div>
          {steps.length > 0 && (
            <div className="mym-reg-mobile-steps" aria-hidden="true">
              {steps.map(step => (
                <div key={step.title} className="mym-reg-mobile-step">
                  <span
                    className={classNames(
                      'mym-tone-icon mym-tone-icon-sm',
                      step.done ? 'mym-tone-success' : 'mym-tone-navy'
                    )}
                  >
                    <FontAwesomeIcon icon={step.icon} />
                  </span>
                  {step.shortTitle || step.title}
                </div>
              ))}
            </div>
          )}
          <div className="mym-reg-card">{children}</div>
          {footer}
          <div className="mym-reg-legal">
            <span>© {new Date().getFullYear()} MapYourMeds</span>
            <Link to="/privacy-policy">Privacy policy</Link>
          </div>
        </div>
      </main>
    </div>
  </div>
);

RegistrationLayout.propTypes = {
  title: PropTypes.node.isRequired,
  subtitle: PropTypes.node,
  hero: PropTypes.node,
  features: PropTypes.arrayOf(itemPropType),
  steps: PropTypes.arrayOf(itemPropType),
  size: PropTypes.oneOf(['wide', 'narrow']),
  footer: PropTypes.node,
  children: PropTypes.node.isRequired
};

export default RegistrationLayout;
